import { IcChevron } from '@assets/icons/24x24';
import { Box, HStack, Icon, Text } from '@chakra-ui/react';
import MenuList from '@components/common/menu/MenuList';
import { useAppSelector } from '@hooks/redux.hooks';
import useClickOutside from '@hooks/useClickOutside';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { selectActiveGlobalScreenConfiguration } from '@redux/config/config.selectors';
import { selectContext } from '@redux/settings/settings.selectors';
import { usePatchContextMutation } from '@services/config/setting.api';
import { FormattedMessage } from 'react-intl';

import { ContextEnum } from '@/types/config/screenConfiguration.types';

import CockpitTime from './CockpitTime';
import NotificationSounds from './panels/notification/NotificationSounds';
import ScreenButtons from './panels/ScreenButtons';

export default function CockpitTitle() {
  const currentContext = useAppSelector(selectContext);
  const { login } = useAppSelector(selectActiveUser);
  const [patchContext] = usePatchContextMutation();
  const { ref, isOpen, close, toggle } = useClickOutside();
  const activeGlobalScreenConfiguration = useAppSelector(selectActiveGlobalScreenConfiguration);

  const handlePatchContext = (context: ContextEnum) => {
    patchContext({ login: login, context })
      .unwrap()
      .then(() => close());
  };

  const contextList = Object.values(ContextEnum).filter((context) =>
    activeGlobalScreenConfiguration?.screenConfigurations.some((sc) => sc.context === context && sc.gridLayout),
  );

  const menuItems = Object.fromEntries(
    contextList.map((currentValue) => [
      currentValue,
      {
        label: `screenConfiguration.context.${currentValue}`,
        isDisabled: currentContext === currentValue,
        isActive: currentContext === currentValue,
        onAction: () => handlePatchContext(currentValue),
      },
    ]),
  );

  const canChangeContext = contextList.length > 1;

  return (
    <HStack
      gap={0}
      paddingX="standardXS"
      height="48px"
      position="absolute"
      zIndex={3}
      left="50%"
      top="6px"
      transform="translateX(-50%)"
      backgroundColor="neutral.900-op80"
      userSelect="none"
    >
      <HStack
        position="relative"
        gap="tightM"
        marginRight="standardXS"
        cursor={canChangeContext ? 'pointer' : 'default'}
        height="100%"
        className="group"
        onClick={() => canChangeContext && toggle()}
        ref={ref}
      >
        <Text color="neutral.white" textStyle="screenMed" textTransform="uppercase">
          <FormattedMessage id={`screenConfiguration.context.${currentContext}`} />
        </Text>
        <Icon
          as={IcChevron}
          boxSize="24px"
          color="neutral.300"
          transform="rotate(-90deg)"
          _groupHover={{ color: canChangeContext ? 'skyBlue' : 'neutral.300' }}
        />
        {isOpen && activeGlobalScreenConfiguration && (
          <Box backgroundColor="neutral.900" position="absolute" left="-12px" top={6}>
            <MenuList buttonWidth="332px" hideBorder menuItems={menuItems} selectedOptions={[`${currentContext}`]} />
          </Box>
        )}
      </HStack>
      <ScreenButtons />
      <CockpitTime />
      <NotificationSounds />
    </HStack>
  );
}
